import { ChangeEvent } from '@faro/realtime-services';
import {
    ActivityCosts,
    ActivityCostsAdjustments,
    ActivityCostsTotals,
    StudyEntityType,
} from '@faro/study-designer-model';
import { ConsumerQueryOptions, useRealtimeQuery, UseRealtimeQueryResult } from '../../package/realtime-query';
import { registerStudyQueryKey } from '../../query/useStudyInactiveQueryInvalidation';
import { RevisionQueryParameter } from '../parameter';
import { useServiceContext } from '../ServiceContext';
import { activityCostsClient } from './ActivityCostsClient';

registerStudyQueryKey('activityCosts');
registerStudyQueryKey('activityCostsTotals');
registerStudyQueryKey('activityCostsTotalsAdjusted');

export interface ActivityCostsDataQuery extends RevisionQueryParameter {
    readonly studyId: string;
    readonly scheduleOfActivitiesId: string | undefined;
}

export interface ActivityCostsTotalsQuery extends RevisionQueryParameter {
    readonly studyId: string;
    readonly scheduleOfActivitiesId: string | undefined;
}

export interface ActivityCostsTotalsWithAdjustmentsQuery {
    readonly studyId: string;
    readonly adjustments: ActivityCostsAdjustments;
}

const metricsEntityTypes = [
    StudyEntityType.Activity,
    StudyEntityType.ActivityGroup,
    StudyEntityType.Epoch,
    StudyEntityType.ScheduledDay,
    StudyEntityType.ScheduleOfActivities,
    StudyEntityType.Stage,
    StudyEntityType.Cycle,
    StudyEntityType.StudyAssessmentMeasurement,
    StudyEntityType.IntraDaySchedule,
];

export const useActivityCosts = (
    query: ActivityCostsDataQuery,
    options?: ConsumerQueryOptions<ActivityCosts[]>
): UseRealtimeQueryResult<ActivityCosts[], Error> => {
    const { studyId, scheduleOfActivitiesId, revision } = query;
    const serviceContext = useServiceContext();

    const queryKey = ['activityCosts', studyId, scheduleOfActivitiesId, revision];
    const events = serviceContext.changeEventService.events;
    const queryFn = async () => {
        return await activityCostsClient.get({ studyId, scheduleOfActivitiesId, revision });
    };
    const refetchWhen = (event: ChangeEvent<any>) => {
        return event.scope === studyId && (metricsEntityTypes ?? []).includes(event.type);
    };

    return useRealtimeQuery({
        ...options,
        queryKey,
        events,
        queryFn,
        refetchWhen,
        refetchOnMount: true,
        enabled: scheduleOfActivitiesId !== undefined && (options?.enabled ?? true),
    });
};

/**
 * Hook to fetch activity costs totals for a given study and schedule of activities
 * Uses useRealtimeQuery to automatically refetch when relevant entities change
 */
export const useActivityCostsTotals = (
    query: ActivityCostsTotalsQuery,
    options?: ConsumerQueryOptions<ActivityCostsTotals>
): UseRealtimeQueryResult<ActivityCostsTotals, Error> => {
    const { studyId, scheduleOfActivitiesId, revision } = query;
    const serviceContext = useServiceContext();

    const queryKey = ['activityCostsTotals', studyId, scheduleOfActivitiesId, revision];
    const events = serviceContext.changeEventService.events;
    const queryFn = async () => {
        return await activityCostsClient.getTotals({
            studyId,
            scheduleOfActivitiesId,
            revision,
        });
    };
    const refetchWhen = (event: ChangeEvent<any>) => {
        return event.scope === studyId && (metricsEntityTypes ?? []).includes(event.type);
    };

    return useRealtimeQuery({
        ...options,
        queryKey,
        events,
        queryFn,
        refetchWhen,
        refetchOnMount: true,
        enabled: scheduleOfActivitiesId !== undefined && (options?.enabled ?? true),
    });
};

/**
 * Hook to fetch activity costs totals with adjustments applied
 * Uses useRealtimeQuery to automatically refetch when relevant entities change
 * Note: revision is included in the adjustments object (ActivityCostsAdjustments.revision)
 */
export const useActivityCostsTotalsWithAdjustments = (
    query: ActivityCostsTotalsWithAdjustmentsQuery,
    options?: ConsumerQueryOptions<ActivityCostsTotals>
): UseRealtimeQueryResult<ActivityCostsTotals, Error> => {
    const { studyId, adjustments } = query;
    const serviceContext = useServiceContext();

    const queryKey = ['activityCostsTotalsAdjusted', studyId, JSON.stringify(adjustments)];
    const events = serviceContext.changeEventService.events;
    const queryFn = async () => {
        return await activityCostsClient.getTotalsWithAdjustments({
            studyId,
            adjustments: adjustments!,
        });
    };
    const refetchWhen = (event: ChangeEvent<any>) => {
        return event.scope === studyId && (metricsEntityTypes ?? []).includes(event.type);
    };

    return useRealtimeQuery({
        ...options,
        queryKey,
        events,
        queryFn,
        refetchWhen,
        refetchOnMount: true,
        enabled: !!studyId && !!adjustments && !!adjustments.scheduleOfActivitiesId && (options?.enabled ?? true),
    });
};
